import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { TrustPill } from "../components/TrustPill";
import { ExecutiveKpiCard } from "../components/reports/ExecutiveKpiCard";
import { FreshnessTable } from "../components/reports/FreshnessTable";
import { SiteExecutiveTable } from "../components/reports/SiteExecutiveTable";
import { WatchlistPanel } from "../components/reports/WatchlistPanel";
import { ActionClassLegend } from "../components/reports/ActionClassLegend";
import type { ExecutiveSeoSnapshotResponse } from "../lib/executive-seo-types";

const STATUS_BADGE: Record<string, { label: string; tone: string }> = {
  too_early: { label: "Too early", tone: "bg-slate-100 text-slate-800 ring-slate-300" },
  early_visibility: { label: "Early visibility", tone: "bg-amber-50 text-amber-800 ring-amber-200" },
  meaningful_seo_signal: { label: "Meaningful SEO signal", tone: "bg-sky-50 text-sky-800 ring-sky-200" },
  conversion_signal: { label: "Conversion signal", tone: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
  monetization_candidate: { label: "Monetization candidate", tone: "bg-emerald-100 text-emerald-900 ring-emerald-300" },
};

export function ExecutiveSeoReportPage(): React.ReactElement {
  const q = useQuery({
    queryKey: ["executive-seo-report"],
    queryFn: () => api<ExecutiveSeoSnapshotResponse>("/api/dashboard/reports/executive-seo"),
    refetchInterval: 5 * 60_000,
  });

  if (q.isLoading) return <div className="p-8 text-ink-500">Loading executive snapshot…</div>;
  if (q.error) {
    return (
      <div className="p-8 bg-rose-50 border border-rose-200 rounded-lg text-rose-800">
        Failed to load: {(q.error as Error).message}
      </div>
    );
  }
  if (!q.data) return <div className="p-8">No data.</div>;
  const data = q.data;

  const status = STATUS_BADGE[data.portfolioStatus.status] ?? STATUS_BADGE.too_early;

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `executive-seo-snapshot-${data.asOf}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const copySummary = async () => {
    const lines = [
      `Executive SEO Snapshot — ${data.asOf}`,
      `Status: ${status.label}`,
      `Real leads 30d: ${data.businessOutcomes.realLeads30d}`,
      `GSC impressions/clicks 30d: ${data.gscVisibility.impressions30d.toLocaleString()} / ${data.gscVisibility.clicks30d}`,
      `Organic GA4 sessions 30d: ${data.ga4Traffic.organicSessions30d}`,
      `Desktop top-10: ${data.rankings.desktop.top10} · top-30: ${data.rankings.desktop.top30}`,
      `Mobile top-10: ${data.rankings.mobile.top10} · top-30: ${data.rankings.mobile.top30}`,
    ];
    try { await navigator.clipboard.writeText(lines.join("\n")); }
    catch { /* clipboard unavailable */ }
  };

  return (
    <div className="space-y-6">
      {/* A. Hero / Executive verdict */}
      <div className="bg-white rounded-xl border border-ink-100 shadow-sm p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-display font-bold">Executive SEO &amp; Traffic Snapshot</h1>
              <TrustPill label={data.portfolioStatus.trustLabel} size="md" />
            </div>
            <p className="text-sm text-ink-600">
              Point-in-time portfolio view. Read-only. No refreshes or paid probes.
            </p>
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ring-1 ring-inset ${status.tone} text-sm font-medium`}>
              {status.label}
            </div>
            <p className="text-base text-ink-900 max-w-3xl">{data.portfolioStatus.headline}</p>
            <p className="text-sm text-ink-700 max-w-3xl">
              <strong>Operator recommendation:</strong> {data.portfolioStatus.operatorRecommendation}
            </p>
            <div className="text-xs text-ink-500 font-mono">
              as-of {data.asOf} · window {data.windowDays}d · generated {new Date(data.generatedAt).toLocaleString()}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <button onClick={copySummary} className="px-3 py-1.5 text-sm rounded border border-ink-200 hover:bg-ink-50">Copy summary</button>
            <button onClick={downloadJson} className="px-3 py-1.5 text-sm rounded border border-ink-200 hover:bg-ink-50">Download JSON</button>
            <button onClick={() => window.print()} className="px-3 py-1.5 text-sm rounded border border-ink-200 hover:bg-ink-50">Print</button>
          </div>
        </div>
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer text-ink-700 hover:text-ink-900">What I would NOT do today</summary>
          <ul className="mt-2 text-sm text-ink-700 space-y-1 list-disc pl-5">
            {data.portfolioStatus.whatNotToDo.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        </details>
      </div>

      {/* B. KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <ExecutiveKpiCard
          label="Real leads 30d"
          value={data.businessOutcomes.realLeads30d}
          trustLabel={data.businessOutcomes.trustLabel}
          source="leads (is_test=false)"
          tone={data.businessOutcomes.realLeads30d > 0 ? "good" : "warn"}
        />
        <ExecutiveKpiCard
          label="GSC clicks 30d"
          value={data.gscVisibility.clicks30d.toLocaleString()}
          trustLabel={data.gscVisibility.trustLabel}
          source={data.gscVisibility.source}
          tone={data.gscVisibility.clicks30d > 0 ? "good" : "warn"}
        />
        <ExecutiveKpiCard
          label="GSC impressions 30d"
          value={data.gscVisibility.impressions30d.toLocaleString()}
          trustLabel={data.gscVisibility.trustLabel}
          source={data.gscVisibility.source}
        />
        <ExecutiveKpiCard
          label="Organic sessions 30d"
          value={data.ga4Traffic.organicSessions30d.toLocaleString()}
          trustLabel={data.ga4Traffic.trustLabel}
          source="ga4_traffic_source_daily"
        />
        <ExecutiveKpiCard
          label="Desktop top-10"
          value={data.rankings.desktop.top10}
          trustLabel={data.rankings.trustLabel}
          source="DataForSEO desktop"
        />
        <ExecutiveKpiCard
          label="Mobile top-10"
          value={data.rankings.mobile.top10}
          trustLabel={data.rankings.mobile.snapshotsAvailable === 0 ? { level: "blocked", reason: "no mobile data" } : { level: "directional", reason: "snapshot only" }}
          source="DataForSEO mobile"
          tone={data.rankings.mobile.snapshotsAvailable === 0 ? "warn" : "neutral"}
        />
        <ExecutiveKpiCard
          label="Sites with SEO signal"
          value={data.siteTable.filter((r) => r.gscImpressions30d > 0 || r.desktopTop100 > 0).length}
          trustLabel={{ level: "directional", reason: "computed from legacy GSC + DFS desktop" }}
          source="composite"
        />
        <ExecutiveKpiCard
          label="Lessee-ready sites"
          value={data.businessOutcomes.monetizationReadySites}
          trustLabel={{ level: "blocked", reason: data.businessOutcomes.monetizationReadyStatus, caveat: data.businessOutcomes.monetizationReadyReason }}
          source="per-site operator review"
          tone="warn"
          hint={data.businessOutcomes.monetizationReadyReason}
        />
      </div>

      {/* C. Data freshness */}
      <section>
        <h2 className="text-lg font-display font-bold mb-3">Data Freshness &amp; Trust</h2>
        <FreshnessTable
          surfaces={[
            { ...data.freshness.leads, surface: "leads (is_test=false)" },
            data.freshness.gscQueryPageDaily,
            data.freshness.legacyGscRankings,
            data.freshness.ga4TrafficSourceDaily,
            data.freshness.ga4Daily,
            data.freshness.desktopRankings,
            data.freshness.mobileRankings,
            data.freshness.serpSnapshots,
            data.freshness.auditRuns,
            data.freshness.pageIndexStatus,
            data.freshness.officerKim,
          ]}
        />
      </section>

      {/* D. Business outcomes */}
      <section>
        <h2 className="text-lg font-display font-bold mb-3">Business Outcomes (real leads only)</h2>
        {data.businessOutcomes.realLeads30d === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-900">
            <strong>No real homeowner lead signal has been confirmed in this window.</strong>
            <div className="text-sm mt-1">
              {data.businessOutcomes.testsExcluded} test rows excluded from this view (sentinels, test forms, test calls).
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ExecutiveKpiCard label="Forms 30d" value={data.businessOutcomes.realForms30d} tone="good" trustLabel={data.businessOutcomes.trustLabel} />
            <ExecutiveKpiCard label="Calls 30d" value={data.businessOutcomes.realCalls30d} tone="good" trustLabel={data.businessOutcomes.trustLabel} />
            <ExecutiveKpiCard label="Calls ≥60s" value={data.businessOutcomes.realCalls60s30d} tone="good" trustLabel={data.businessOutcomes.trustLabel} />
            <ExecutiveKpiCard label="Voicemails" value={data.businessOutcomes.realVoicemails30d} tone="good" trustLabel={data.businessOutcomes.trustLabel} />
            <ExecutiveKpiCard label="Qualified" value={data.businessOutcomes.qualifiedLeads30d} tone="good" trustLabel={data.businessOutcomes.trustLabel} />
            <ExecutiveKpiCard label="Revenue" value={`$${data.businessOutcomes.revenue30d}`} tone="neutral" trustLabel={data.businessOutcomes.trustLabel} />
          </div>
        )}
      </section>

      {/* E. GSC visibility */}
      <section>
        <h2 className="text-lg font-display font-bold mb-3 flex items-center gap-2">
          GSC Visibility
          <TrustPill label={data.gscVisibility.trustLabel} />
        </h2>
        {data.gscVisibility.source === "legacy_rankings" && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 mb-3">
            Using legacy GSC rows in <code>rankings</code>. Page-level interpretation is limited until K11 migrates dashboard/report queries to <code>gsc_query_page_daily</code>.
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
          {data.gscVisibility.positionBands.map((b) => (
            <div key={b.band} className="bg-white border border-ink-100 rounded-lg p-3">
              <div className="text-xs uppercase tracking-wider text-ink-500">Position {b.band}</div>
              <div className="text-xl font-display font-bold mt-1">{b.impressions.toLocaleString()}</div>
              <div className="text-xs text-ink-500">impressions</div>
            </div>
          ))}
        </div>
        <details className="mt-2">
          <summary className="cursor-pointer text-sm text-ink-700 hover:text-ink-900">Top 25 queries (30d)</summary>
          <div className="overflow-x-auto bg-white rounded-xl border border-ink-100 mt-2">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-xs uppercase tracking-wider text-ink-600">
                <tr><th className="text-left px-3 py-2">keyword</th><th className="text-right px-3 py-2">imp</th><th className="text-right px-3 py-2">clicks</th><th className="text-right px-3 py-2">avg pos</th><th className="text-right px-3 py-2">sites</th></tr>
              </thead>
              <tbody>
                {data.gscVisibility.topQueries.map((q, i) => (
                  <tr key={i} className="border-t border-ink-100">
                    <td className="px-3 py-1.5">{q.keyword}</td>
                    <td className="px-3 py-1.5 text-right">{q.impressions.toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right">{q.clicks}</td>
                    <td className="px-3 py-1.5 text-right">{q.avgPos?.toFixed(1) ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right">{q.sites}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      </section>

      {/* F. GA4 traffic */}
      <section>
        <h2 className="text-lg font-display font-bold mb-3 flex items-center gap-2">
          GA4 Traffic Quality
          <TrustPill label={data.ga4Traffic.trustLabel} />
        </h2>
        {data.ga4Traffic.caveats.map((c, i) => (
          <div key={i} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-2">{c}</div>
        ))}
        <div className="overflow-x-auto bg-white rounded-xl border border-ink-100">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-xs uppercase tracking-wider text-ink-600">
              <tr><th className="text-left px-3 py-2">channel</th><th className="text-right px-3 py-2">sessions</th><th className="text-right px-3 py-2">engaged</th><th className="text-right px-3 py-2">sites</th></tr>
            </thead>
            <tbody>
              {data.ga4Traffic.channelSummary.map((c) => (
                <tr key={c.channel} className="border-t border-ink-100">
                  <td className="px-3 py-1.5">{c.channel}</td>
                  <td className="px-3 py-1.5 text-right">{c.sessions.toLocaleString()}</td>
                  <td className="px-3 py-1.5 text-right">{c.engagedSessions.toLocaleString()}</td>
                  <td className="px-3 py-1.5 text-right">{c.sites}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* G. Ranking snapshot */}
      <section>
        <h2 className="text-lg font-display font-bold mb-3 flex items-center gap-2">
          Ranking Snapshot
          <TrustPill label={data.rankings.trustLabel} />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <RankPanel title="Desktop (DataForSEO)" dist={data.rankings.desktop} movementNote={data.rankings.movement.desktop.note} />
          <RankPanel title="Mobile (DataForSEO)" dist={data.rankings.mobile} movementNote={data.rankings.movement.mobile.note} />
        </div>
      </section>

      {/* H. SERP feature */}
      <section>
        <h2 className="text-lg font-display font-bold mb-3 flex items-center gap-2">
          SERP Features
          <TrustPill label={data.serpFeatures.trustLabel} />
        </h2>
        {data.serpFeatures.caveats.map((c, i) => (
          <div key={i} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-2">{c}</div>
        ))}
      </section>

      {/* J. Site table */}
      <section>
        <h2 className="text-lg font-display font-bold mb-3">Site-level executive view</h2>
        <SiteExecutiveTable rows={data.siteTable} />
      </section>

      {/* K. Watchlists */}
      <section>
        <h2 className="text-lg font-display font-bold mb-3">Watchlists</h2>
        <WatchlistPanel watchlists={data.watchlists} />
      </section>

      {/* L. Risks */}
      <section>
        <h2 className="text-lg font-display font-bold mb-3">Risk register</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.risks.map((r, i) => (
            <div key={i} className={`rounded-xl border p-3 ${r.severity === "high" ? "bg-rose-50 border-rose-200" : r.severity === "medium" ? "bg-amber-50 border-amber-200" : "bg-white border-ink-100"}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-medium text-ink-900">{r.label}</span>
                <span className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-wider ${r.severity === "high" ? "text-rose-700" : r.severity === "medium" ? "text-amber-700" : "text-ink-500"}`}>{r.severity}</span>
                  <TrustPill label={r.trustLabel} />
                </span>
              </div>
              <div className="text-sm text-ink-700">{r.description}</div>
            </div>
          ))}
        </div>
      </section>

      {/* M. Allowed actions */}
      <section>
        <h2 className="text-lg font-display font-bold mb-3">Allowed vs Blocked Actions</h2>
        <ActionClassLegend
          readOnly={data.allowedActions.readOnly}
          writeFreeRequiresAuth={data.allowedActions.writeFreeRequiresAuth}
          paidWriteRequiresAuth={data.allowedActions.paidWriteRequiresAuth}
          blocked={data.allowedActions.blocked}
        />
        <p className="text-xs text-ink-500 mt-3 italic">
          Action buttons on this page are read-only or client-side. Any WRITE / PAID action requires explicit operator authorization elsewhere.
        </p>
      </section>
    </div>
  );
}

function RankPanel(props: { title: string; dist: ExecutiveSeoSnapshotResponse["rankings"]["desktop"]; movementNote: string }): React.ReactElement {
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-4">
      <div className="text-sm font-medium text-ink-800 mb-2">{props.title}</div>
      <div className="grid grid-cols-5 gap-2">
        {[
          { lbl: "top 3", v: props.dist.top3 },
          { lbl: "top 10", v: props.dist.top10 },
          { lbl: "top 30", v: props.dist.top30 },
          { lbl: "top 100", v: props.dist.top100 },
          { lbl: "100+", v: props.dist.beyond100 },
        ].map((c) => (
          <div key={c.lbl} className="text-center">
            <div className="text-xs uppercase tracking-wider text-ink-500">{c.lbl}</div>
            <div className="text-xl font-display font-bold">{c.v}</div>
          </div>
        ))}
      </div>
      <div className="text-xs text-ink-500 mt-2">
        {props.dist.distinctKeywords} keywords across {props.dist.sites} sites. {props.movementNote}.
      </div>
    </div>
  );
}
